import { settings } from './settings';

type ConsoleLevel = 'debug' | 'info' | 'warn' | 'error';
type LogLevelKey = ConsoleLevel | 'off';

enum LogLevel {
	debug = 0,
	info = 1,
	warn = 2,
	error = 3,
	off = 99,
}

function toLogLevelKey(value: string): LogLevelKey {
	return (value in LogLevel ? value : 'warn') as LogLevelKey;
}

class Logger {
	static #namespace = settings.appName;
	#name: string;
	#level: LogLevelKey;
	propsCallback: (() => string) | null;

	constructor({
		logger = '',
		propsCallback = null,
		setLevel = Logger.#defaultLevel(),
	}: {
		logger?: string;
		propsCallback?: (() => string) | null;
		setLevel?: LogLevelKey;
	} = {}) {
		this.#name = `${Logger.#namespace}/${logger}`;
		this.#level = setLevel;
		this.propsCallback = propsCallback;
	}

	static #defaultLevel(): LogLevelKey {
		const isDebugMode = window.localStorage.getItem('debugMode') === 'true';
		return isDebugMode ? 'debug' : toLogLevelKey(settings.logLevel);
	}

	#buildMessage(...args: unknown[]): unknown[] {
		const message: unknown[] = [`[${this.#name}]`, ...args];
		if (typeof this.propsCallback === 'function') {
			message.push(this.propsCallback());
		}
		return message;
	}

	#emit(level: ConsoleLevel, ...args: unknown[]): void {
		if (LogLevel[level] >= LogLevel[this.#level]) {
			console[level](...this.#buildMessage(...args));
		}
	}

	debug(...args: unknown[]): void {
		this.#emit('debug', ...args);
	}

	info(...args: unknown[]): void {
		this.#emit('info', ...args);
	}

	warn(...args: unknown[]): void {
		this.#emit('warn', ...args);
	}

	error(...args: unknown[]): void {
		this.#emit('error', ...args);
	}
}

export { Logger };
